<template>
    <div>
        <b-modal 
			@hidden="hideModal" 
			ref="weldRequests" 
			id="weldRequests" 
			:title="tableNameTranslated"
            :ok-only="true"
            :ok-variant="'secondary'"
            :ok-title="$t('misc.cancel')"
			size="huge"
		>
            <data-table
                v-if="tableLoaded"
                :exportable="false" 
                title=""
                :rows="getData.rowData"
                :columns="columnData"
                :perPage="perPage"
                :rowsCount="rowsCount"
                :currentPage="currentPage"
                :pageCount="pageCount"
                :rowChangeSelectOptions="getData.rowChangeSelectOptions"
                :disabledInputs="!this.$store.getters.hasPermission(['project_ndt_welds'], ['update'])"
                @changePage="changePage"
                @searchEvent="searchEvent"
                @orderByEvent="orderByEvent"
                @rowClickDelete="onDeleteRow"
                @rowChangeTextInput="onEditRow"
                @rowChangeDate="onEditRow"
                @rowChangeSelect="onEditRow"
                @rowChangeTextAreaInput="onEditRow"
            >
            </data-table>

            <b-btn 
                v-if="this.$store.getters.hasPermission(['project_ndt_welds'], ['create'])"
                class="btn btn-default mt-3 mr-2" 
                @click="onAddRow"
            >
                <i class="fa fa-plus-circle mr-1"></i> {{$t('general.welding.weld_requests.create_button')}}
            </b-btn>

            <b-btn 
                v-if="this.$store.getters.hasPermission(['project_ndt_welds'], ['create']) && getData.rowData.length > 0"
                class="btn btn-danger mt-3 text-white" 
                @click="onMakeRequest"
            >
                <i class="fa fa-arrow-circle-right" aria-hidden="true"></i> {{$t('general.welding.weld_requests.make_request.title')}}
            </b-btn>
        </b-modal>

        <weld-request-modal
            ref="weldRequestModal"
            :testingTypes="testingTypes"
            @onLoadTable="onLoadTable"   
            @onLoadMainTable="onLoadMainTable"   
        >
        </weld-request-modal>

        <confirm-dialog ref="deleteModal" :title="actionTitle" @buttonClick="deleteAction">
            {{ $t('messages.delete') }}
        </confirm-dialog>

        <confirm-dialog ref="makeRequestModal" :title="actionTitle" @buttonClick="onMakeRequestAction">
            {{ $t('messages.make_request') }}
        </confirm-dialog>
    </div>
</template>

<script>
import Vue from 'vue';
import http from '@/libs/http';
import DataTable from '@/components/global/tables/datatables/DataTable.vue';
import ConfirmDialog from '@/components/global/modals/ConfirmDialog.vue';
import miniToastr from 'mini-toastr';
import WeldRequestModal from '@/components/global/modals/WeldRequestModal.vue';

export default {
    name: 'weld_requests',
    components: {
        DataTable,
        WeldRequestModal,
        ConfirmDialog,
    },
    props: {
        testingTypes: {
            type: Object,
            default: function () {
                return null;
            },
        },
    },
    data() {
        return {
            tableName: 'weld_request',
            tableNameTranslated: Vue.i18n.translate('general.welding.weld_requests.title2'),
            actionTitle: '',

            row: null,
            tableLoaded: false,
            perPage: 0,
            rowsCount: 0,
            pageCount: 1,
            currentPage: 1,
            sortType: 'asc',
            sortBy: 'id',
            searchInput: '',

            getData: {
                rowData: [],
                rowChangeSelectOptions: {
                    company_id: [],
                },
            },
            columnData: [
                {
                    label: Vue.i18n.translate('misc.actions'),
                    field: '',
                    showDeleteButton: true,
                },
                {
                    label: Vue.i18n.translate('table.line_no'),
                    field: 'weld.drawing.line.line_number',
                    numeric: false,
                    html: false,
                },
                {
                    label: Vue.i18n.translate('table.iso_no'),
                    field: 'weld.drawing.iso_no',
                    numeric: false,
                    html: false,
                },
                {
                    label: Vue.i18n.translate('table.page_no'),
                    field: 'weld.drawing.page',
                    numeric: false,
                    html: false,
                },
                {
                    label: Vue.i18n.translate('table.spool_no'),
                    field: 'weld.spool.spool_no',
                    numeric: false,
                    html: false,
                },
                {
                    label: Vue.i18n.translate('table.line_class'),
                    field: 'weld.drawing.line.line_class',
                    numeric: false,
                    html: false,
                },
                {
                    label: Vue.i18n.translate('table.weld_material'),
                    field: 'weld.weld_material.value',
                    numeric: false,
                    html: false,
                },
                {
                    label: Vue.i18n.translate('table.weld_no'),
                    field: 'weld.weld_no',
                    numeric: false,
                    html: false,
                },
                {
                    label: Vue.i18n.translate('table.weld_type'),
                    field: 'weld.project_setting.value',
                    numeric: false,
                    html: false,
                },
                {
                    label: Vue.i18n.translate('table.weld_place'),
                    field: 'weld.weld_place',
                    numeric: false,
                    html: false,
                },
                {
                    label: Vue.i18n.translate('table.weld_date'),
                    field: 'weld.welder_date_1',
                    numeric: false,
                    html: false,
                },
                {
                    label: Vue.i18n.translate('table.welder1'),
                    field: 'weld.welder1.fullname',
                    numeric: false,
                    html: false,
                },
                {
                    label: Vue.i18n.translate('table.welder2'),
                    field: 'weld.welder2.fullname',
                    numeric: false,
                    html: false,
                },
                {
                    label: Vue.i18n.translate('table.filler_material'),
                    field: 'weld.filler_material',
                    numeric: false,
                    html: false,
                },
                {
                    label: Vue.i18n.translate('table.ped_category'),
                    field: 'weld.drawing.line.ped_category',
                    numeric: false,
                    html: false,
                },
                {
                    label: Vue.i18n.translate('table.weld_size'),
                    field: 'weld.weld_size',
                    numeric: false,
                    html: false,
                },
                {
                    label: Vue.i18n.translate('table.weld_dimension'),
                    field: 'weld.weld_dimension',
                    numeric: false,
                    html: false,
                },
                {
                    label: Vue.i18n.translate('table.progress_status'),
                    field: 'weld.drawing_progress_status',
                    numeric: false,
                    html: false,
                },
                {
                    label: Vue.i18n.translate('table.inspection'),
                    field: 'weld.inspection.value',
                    numeric: false,
                    html: false,
                },
                {
                    label: Vue.i18n.translate('table.testing_type'),
                    field: 'testing_typeV',
                    numeric: false,
                    html: false,
                },
                {
                    label: Vue.i18n.translate('table.required_testing_date'),
                    field: 'required_testing_date',
                    numeric: false,
                    html: false,
                    showDateInput: true,
                },
                {
                    label: Vue.i18n.translate('table.location'),
                    field: 'location',
                    width: '170px',
                    numeric: false,
                    html: false,
                    showTextInput: true,
                },
                {
                    label: Vue.i18n.translate('table.company'),
                    width: '250px',
                    field: 'company_id',
                    numeric: false,
                    html: false,
                    showSelect: true,
                },
                {
                    label: Vue.i18n.translate('table.remark_no_1'),
                    field: 'remark_no_1',
                    width: '200px',
                    numeric: false,
                    html: false,
                    showTextAreaInput: true,
                },
            ],
        }
    },
    mounted () {
    },
    methods: {
        successMsg(msg) {
            miniToastr.success(msg, Vue.i18n.translate('messages.success'), 5000);
        },
        errorMsg(msg) {
            miniToastr.error(msg, Vue.i18n.translate('messages.error'), 5000);
        },
        orderByEvent (column, sort_type) {
            this.sortType = sort_type;
            this.sortBy = column.field;
            this.onLoadTable();
        },
        searchEvent (searchInput) {
            this.searchInput = searchInput;
            this.onLoadTable();
        },
        changePage (page) {
            this.currentPage = page;
            this.onLoadTable();
        },
        onLoadTable () {
            http.route(this.tableName + '?project_id=' + this.$route.params.id + '&search=' + this.searchInput + '&page=' + this.currentPage + '&sort_by=' + this.sortBy + '&sort_order=' + this.sortType + '&filters[requested]=0').get().then(response => {
                if (!response.isError) {
                    this.perPage     = response.pagination.limit;
                    this.rowsCount   = response.pagination.totalEntries;
                    this.currentPage = response.pagination.currentPage;
                    this.pageCount   = response.pagination.totalPages;
                    var data = response.data;
                    data.forEach((field, index) => {
                        if (typeof this.testingTypes[field.testing_type] != 'undefined') {
                            field.testing_typeV = this.testingTypes[field.testing_type].name;
                        }

                        if (field.weld.spool == null) {
                            field.weld.spool = { 
                                'spool_no': '' 
                            };
                        }

                        if (field.weld.project_setting == null) {
                            field.weld.project_setting = { 
                                'value': '' 
                            };
                        }

                        if (field.weld.weld_material == null) {
                            field.weld.weld_material = { 
                                'value': '' 
                            };
                        }

                        if (field.weld.inspection == null) {
                            field.weld.inspection = { 
                                'value': '' 
                            };
                        }

                        if (field.weld.welder1 == null) {
                            field.weld.welder1 = { 
                                'fullname': '' 
                            };
                        }

                        if (field.weld.welder2 == null) {
                            field.weld.welder2 = { 
                                'fullname': '' 
                            };
                        }
                    });

                    this.getData.rowData = data;
                    this.tableLoaded = true;
                } else {
                    Object.keys(response.errorMessage).forEach(key => {
                        this.errorMsg(Vue.i18n.translate(response.errorMessage[key]));
                    });
                }
            });
        },
        onLoadMainTable () {
            this.$emit('onLoadMainTable');
        },
        showModal () {
            this.onLoadTable();
            this.getCompanySelect();
            this.$refs.weldRequests.show();
        },
        hideModal () {
            this.tableLoaded = false;
        },
        onMakeRequest () {
            this.actionTitle = Vue.i18n.translate('modal.general.make_request');
            this.$refs.makeRequestModal.show();
        },
        onMakeRequestAction (button) {
            if (button == 1) {
                let put_data = {
                    project_id: this.$route.params.id,
                };

                http.route(this.tableName + '_make_request').post(put_data, true)
                .then(response => {
                    if (!response.isError) {
                        if (typeof response.data != 'undefined' && response.data == 1) {
                            this.$store.commit('SET_KEY', 'redirected_from_current_welds');
                            this.$router.push({name: 'ndt_welds'});
                        }

                        this.$refs.makeRequestModal.hide();
                    } else {
                        Object.keys(response.errorMessage).forEach(key => {
                            this.errorMsg(Vue.i18n.translate(response.errorMessage[key]));
                        });
                    }
                });
            } else {
                this.$refs.makeRequestModal.hide();
            }
        },
        onAddRow () {
            this.$refs.weldRequestModal.showModal();
        },
        onEditRow (row, index, column, column_name, value) {
            var put_data = {
                project_id: this.$route.params.id,
            };

            put_data[column_name] = value;

            Object.keys(put_data).forEach(key => {
                if (put_data[key] === '') {
                    put_data[key] = null;
                }
            });

            http.route(this.tableName + '/' + row.id).patch(put_data, true)
            .then(response => {
                if (!response.isError) {
                    this.successMsg(this.tableNameTranslated + Vue.i18n.translate('messages.updated'));
                } else {
                    row[column_name] = null;
                    Object.keys(response.errorMessage).forEach(key => {
                        this.errorMsg(Vue.i18n.translate(response.errorMessage[key]));
                    });
                }
            });
        },
        onDeleteRow (row, index) {
            this.actionTitle = Vue.i18n.translate('modal.general.delete') + ' ' + this.tableNameTranslated;
            this.row = row;
            this.$refs.deleteModal.show();
        },
        deleteAction (button) {
            if (button == 1) {
                let data = http.route(this.tableName + '/' + this.row.id).delete();
                data.then(response => {
                    if (response.isError == false) {
                        if (this.currentPage > 1 && this.getData.rowData.length == 1) {
                            this.currentPage = this.currentPage - 1;
                        }

                        this.onLoadTable();
                        this.onLoadMainTable();
                        this.successMsg(this.tableNameTranslated + Vue.i18n.translate('messages.deleted'));
                    } else {
                        this.errorMsg(Vue.i18n.translate('errors.error_delete'));
                    }
                });
            }

            this.$refs.deleteModal.hide();
        },
        getCompanySelect () {
            http.route('company?project_id=' + this.$route.params.id + '&subcontractors=1').get()
            .then(response => {
                if (!response.isError) {
                    var companies = [];
                    response.data.forEach((field, index) => {
                        companies[index] = {
                            id: field.id,
                            name: field.name,
                        };
                    });
                    this.getData.rowChangeSelectOptions['company_id'] = companies;
                } else {
                    Object.keys(response.errorMessage).forEach(key => {
                        this.errorMsg(Vue.i18n.translate(response.errorMessage[key]));
                    });
                }
            });
        },
    }
}
</script>


// WEBPACK FOOTER //
// src/components/pages/project/welding/current_welds/weld_requests/weld_requests.vue